import React from "react";
import { TextField } from "@mui/material";
import type { Job } from "../../../../../../api/types";
import { ImportTableColumn } from "../../../../../../utilities/types";
import { getValidationErrorKey } from "../../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../../admin/ImportSettings";
import { ColumnContext } from "../ColumnContext";
import { useValidationErrors } from "../../../ValidationErrorsContext";
import { useTextColumnViewModel } from "./textColumn.vm";

export interface TextColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** The field name on the Job object */
	fieldName: keyof Job;
	/** The column identifier */
	columnId: ImportTableColumn;
	/** Input type (text, email, tel, number) */
	inputType?: string;
	/** Whether the field is required */
	required?: boolean;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Custom label override */
	label?: string;
	/** Multiline mode for longer text */
	multiline?: boolean;
	/** Number of rows for multiline */
	rows?: number;
	/** Placeholder text (e.g., for bulk edit mixed values) */
	placeholder?: string;
}

/**
 * Mobile-friendly text input component for ImportTable fields.
 * Simplified version of textColumn that doesn't depend on MRT.
 */
export const TextColumnMobile: React.FC<TextColumnMobileProps> = ({
	job,
	columnContext,
	fieldName,
	columnId,
	inputType = "text",
	required = false,
	disabled = false,
	label,
	multiline = false,
	rows = 1,
	placeholder,
}) => {
	const vm = useTextColumnViewModel(columnContext, columnId);
	const validationErrors = useValidationErrors();
	const errorKey = getValidationErrorKey(columnId, job.id);
	const currentValue = vm.getCurrentValue(job, fieldName);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		vm.handleTextChange(job.id, fieldName, event.target.value);
	};

	const handleBlur = () => {
		if (disabled) return;
		// Get the updated job from context (it was modified in handleChange)
		const updatedJob = columnContext.jobs.find((j) => j.id === job.id);
		if (updatedJob) {
			vm.handleTextBlur(updatedJob, fieldName);
		}
	};

	return (
		<TextField
			size="small"
			type={inputType}
			variant="outlined"
			required={required}
			disabled={disabled}
			fullWidth
			label={label || columnDisplayNames[columnId]}
			value={currentValue}
			placeholder={placeholder}
			error={!!validationErrors?.[errorKey]}
			helperText={validationErrors?.[errorKey]}
			onChange={handleChange}
			onBlur={handleBlur}
			multiline={multiline}
			rows={rows}
		/>
	);
};
